import { useState } from 'react';
import { toast } from 'sonner';
import {
  useTransitionEntityMutation,
  useSetEntityFlagMutation,
  useDeleteEntityMutation,
  useUndeleteEntityMutation,
  useCreateEntityPromotionMutation,
  useDeleteEntityAssetMutation,
  useRestoreEntityAssetMutation,
  useDuplicateEntityMutation,
  EntityStatus,
  EntityFlagField,
} from '@/client/api/listing-management/entity.mutation';
import type { ConfirmAction } from './types';

/**
 * Centralises every mutating listing action behind the shared confirm dialog.
 * Owns the pending `confirm` action, dispatches it on `runConfirm`, and reports
 * a single `busy` flag plus the per-asset busy id used by the media grid.
 */
export function useListingActions({
  eid,
  refetchDetail,
  refetchPromotions,
  refetchAssets,
  onDuplicated,
}: {
  eid: number;
  refetchDetail: () => void;
  refetchPromotions: () => void;
  refetchAssets: () => void;
  /** Called with the new listing's eid after a duplicate. */
  onDuplicated?: (eid: number) => void;
}) {
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);

  const transition = useTransitionEntityMutation();
  const setFlag = useSetEntityFlagMutation();
  const deleteMut = useDeleteEntityMutation();
  const undeleteMut = useUndeleteEntityMutation();
  const boostMut = useCreateEntityPromotionMutation();
  const deleteAssetMut = useDeleteEntityAssetMutation();
  const restoreAssetMut = useRestoreEntityAssetMutation();
  const duplicateMut = useDuplicateEntityMutation();

  const runTransition = async (next: EntityStatus, label: string) => {
    try {
      await transition.mutateAsync({ eid, status: next });
      toast.success(`Listing → ${next}`);
      setConfirm(null);
      // Force-refresh so the status pill/flags reflect the new state now, not
      // on the next background refetch.
      refetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? `Failed to ${label} the listing.`);
    }
  };

  const toggleFlag = async (field: EntityFlagField, current: boolean) => {
    try {
      await setFlag.mutateAsync({ eid, field, value: current ? 0 : 1 });
      toast.success(`${field} ${current ? 'disabled' : 'enabled'}`);
      setConfirm(null);
      refetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update the flag.');
    }
  };

  const runDelete = async () => {
    try {
      await deleteMut.mutateAsync({ eid });
      toast.success('Listing deleted');
      setConfirm(null);
      refetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to delete the listing.');
    }
  };

  const runUndelete = async () => {
    try {
      await undeleteMut.mutateAsync({ eid });
      toast.success('Listing restored');
      setConfirm(null);
      refetchDetail();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to restore the listing.');
    }
  };

  const runBoost = async () => {
    try {
      await boostMut.mutateAsync({ eid });
      toast.success('Listing boosted');
      setConfirm(null);
      refetchPromotions();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to boost the listing.');
    }
  };

  const runAssetDelete = async (asid: number) => {
    try {
      await deleteAssetMut.mutateAsync({ eid, asid });
      toast.success('Photo archived');
      setConfirm(null);
      refetchAssets();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to archive the photo.');
    }
  };

  const runAssetRestore = async (asid: number) => {
    try {
      await restoreAssetMut.mutateAsync({ eid, asid });
      toast.success('Photo restored');
      setConfirm(null);
      refetchAssets();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to restore the photo.');
    }
  };

  const runDuplicate = async (reason?: string) => {
    if (!reason) return;
    try {
      const created = await duplicateMut.mutateAsync({ eid, reason });
      toast.success(`Draft copy created (#${created.eid}).`);
      setConfirm(null);
      // The copy is a different listing, so the admin is taken to it rather
      // than left looking at the original wondering whether it worked.
      if (created?.eid) onDuplicated?.(created.eid);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to duplicate the listing.');
    }
  };

  // Drives the shared confirm dialog: dispatches the pending action on confirm.
  const runConfirm = (reason?: string) => {
    if (!confirm) return;
    switch (confirm.kind) {
      case 'duplicate': return runDuplicate(reason);
      case 'transition': return runTransition(confirm.status, confirm.label);
      case 'delete': return runDelete();
      case 'undelete': return runUndelete();
      case 'boost': return runBoost();
      case 'flag': return toggleFlag(confirm.field, confirm.current);
      case 'asset-delete': return runAssetDelete(confirm.asid);
      case 'asset-restore': return runAssetRestore(confirm.asid);
    }
  };

  const busy =
    transition.isPending || setFlag.isPending || deleteMut.isPending ||
    undeleteMut.isPending || boostMut.isPending || deleteAssetMut.isPending ||
    restoreAssetMut.isPending || duplicateMut.isPending;

  const assetBusyAsid =
    (deleteAssetMut.isPending || restoreAssetMut.isPending) &&
    (confirm?.kind === 'asset-delete' || confirm?.kind === 'asset-restore')
      ? confirm.asid
      : null;

  return {
    confirm,
    setConfirm,
    runConfirm,
    busy,
    assetBusyAsid,
    pending: {
      setFlag: setFlag.isPending,
      boost: boostMut.isPending,
      undelete: undeleteMut.isPending,
    },
  };
}
